import { NgClass, NgIf } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomToastComponent } from '../../components/toast/custom-toast.component';
import { HttpService } from '../../services/http.service';

interface CSQTeamMember {
    id: any;
    memberId: string;
    firstName: string;
    lastName: string;
    email: string;
    mobileNumber: string;
}

interface APIResponse {
    status: boolean;
    message: string;
    data: CSQTeamMember[];
}

@Component({
    selector: 'app-password',
    standalone: true,
    templateUrl: './password.component.html',
    styleUrls: ['./password.component.scss'],
    imports: [ReactiveFormsModule, NgIf, NgClass],
})
export class PasswordComponent implements OnInit {
    @Input() context: 'profile' | 'password' | 'default' = 'default';
    passwordForm: FormGroup;
    showOldPassword = false;
    showNewPassword = false;
    showConfirmPassword = false;
    isSubmitting = false;
    userID: string = "";
    submitted = false;
    formVisible = true;


    constructor(
        private fb: FormBuilder,
        private httpService: HttpService,
        private snackBar: MatSnackBar
    ) {
        this.passwordForm = this.fb.group({
            currentPassword: ['', [Validators.required]],
            // newPassword: ['', [
            //     Validators.required,
            //     Validators.minLength(8),
            //     Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)
            // ]],
            newPassword: ['', [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/
                )
            ]],
            confirmPassword: ['', [Validators.required]],
        }, {
            validator: this.passwordMatchValidator
        });
    }

    ngOnInit(): void {
        const userDetailsString = localStorage.getItem('userDetails');
        if (userDetailsString) {
            const userDetails = JSON.parse(userDetailsString);
            this.userID = userDetails.userId;
        }
    }



    passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
        const formGroup = control as FormGroup;
        const newPassword = formGroup.get('newPassword');
        const confirmPassword = formGroup.get('confirmPassword');

        if (!newPassword || !confirmPassword) {
            return null;
        }

        if (newPassword.value && confirmPassword.value) {
            if (newPassword.value !== confirmPassword.value) {
                confirmPassword.setErrors({ matching: true });
                return { matching: true };
            } else {
                if (confirmPassword.hasError('matching')) {
                    confirmPassword.setErrors(null);
                    if (confirmPassword.hasValidator(Validators.required)) {
                        const errors: ValidationErrors = {};
                        if (!confirmPassword.value) {
                            errors['required'] = true;
                        }
                        confirmPassword.setErrors(Object.keys(errors).length > 0 ? errors : null);
                    }
                }
            }
        }

        return null;
    }

    onCancel(): void {
        this.formVisible = false;
        this.passwordForm.reset();
        this.submitted = false;
        this.isSubmitting = false;

        // Reset validation states
        Object.keys(this.passwordForm.controls).forEach((key) => {
            const control = this.passwordForm.get(key);
            if (control) {
                control.markAsUntouched();
                control.markAsPristine();
                control.setErrors(null);
                control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
            }
        });

        this.showOldPassword = false;
        this.showNewPassword = false;
        this.showConfirmPassword = false;

        this.passwordForm.updateValueAndValidity();
        setTimeout(() => {
            this.formVisible = true;
        }, 0);
    }

    getNewPasswordErrorMessage(): string {
        const newPasswordControl = this.passwordForm.get('newPassword');

        if (!newPasswordControl?.touched || !newPasswordControl?.invalid) {
            return '';
        }

        if (newPasswordControl.hasError('required')) {
            return 'New password is required';
        }

        if (newPasswordControl.hasError('minlength')) {
            return 'Password must be at least 8 characters';
        }

        if (newPasswordControl.hasError('pattern')) {
            const passwordValue = newPasswordControl.value || '';
            const missingCriteria = [];
            if (!/(?=.*[A-Z])/.test(passwordValue)) {
                missingCriteria.push('uppercase');
            }
            if (!/(?=.*[a-z])/.test(passwordValue)) {
                missingCriteria.push('lowercase');
            }
            if (!/(?=.*\d)/.test(passwordValue)) {
                missingCriteria.push('number');
            }
            if (!/(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(passwordValue)) {
                missingCriteria.push('special character');
            }

            if (missingCriteria.length > 0) {
                return `Password must include ${missingCriteria.join(', ')}`;
            }
        }

        return '';
    }

    togglePasswordVisibility(field: 'old' | 'new' | 'confirm'): void {
        if (field === 'old') {
            this.showOldPassword = !this.showOldPassword;
        } else if (field === 'new') {
            this.showNewPassword = !this.showNewPassword;
        } else if (field === 'confirm') {
            this.showConfirmPassword = !this.showConfirmPassword;
        }
    }

    isFieldInvalid(fieldName: string): boolean {
        const field = this.passwordForm.get(fieldName);
        return field ? (field.invalid && (field.dirty || field.touched || this.submitted)) : false;
    }

    onSubmit(): void {
        this.submitted = true;
        Object.keys(this.passwordForm.controls).forEach((key) => {
            const control = this.passwordForm.get(key);
            if (control) {
                control.markAsTouched();
                control.markAsDirty();
            }
        });

        this.passwordForm.updateValueAndValidity();

        if (this.passwordForm.invalid) {
            const currentPassword = this.passwordForm.get('currentPassword');
            const newPassword = this.passwordForm.get('newPassword');
            const confirmPassword = this.passwordForm.get('confirmPassword');
            if (currentPassword?.errors?.['required']) {
            } else if (newPassword?.errors?.['required']) {
            } else if (newPassword?.errors?.['pattern']) {
            } else if (confirmPassword?.errors?.['required']) {
            } else if (confirmPassword?.errors?.['matching']) {
            }
            return;
        }

        this.isSubmitting = true;
        const formValues = this.passwordForm.value;

        this.httpService
            .put<APIResponse>(`settings/change-password/${this.userID}`, formValues)
            .subscribe({
                next: (response) => {
                    this.isSubmitting = false;
                    this.submitted = false;
                    if (response.status) {
                        this.showSuccessToast(response?.message || 'Password changed successfully');
                        this.passwordForm.reset();
                        Object.keys(this.passwordForm.controls).forEach((key) => {
                            const control = this.passwordForm.get(key);
                            if (control) {
                                control.markAsUntouched();
                                control.markAsPristine();
                                control.setErrors(null);
                            }
                        });
                    } else {
                        this.showErrorToast(response?.message || 'Failed to change password');
                    }
                },
                error: (error) => {
                    this.isSubmitting = false;
                    this.submitted = false;
                    this.showErrorToast(error?.message || 'An error occurred while changing password');
                },
            });
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast'],
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast'],
        });
    }

    getConfirmPasswordError(): string {
        const confirmPasswordControl = this.passwordForm.get('confirmPassword');
        if (confirmPasswordControl?.errors?.['matching']) {
            return 'Passwords do not match';
        }
        if (confirmPasswordControl?.errors?.['required']) {
            return 'Confirm password is required';
        }
        return '';
    }
}